import { useCallback, useEffect, useMemo, useState } from "react";
import { ListChecks, RotateCcw, Check, Printer, Plus, Trash2, X, Loader2, Lock } from "lucide-react";
import { getChecklist, setChecklistItem, resetChecklist } from "../lib/storage";
import { useProState } from "../lib/pro";
import ProBadge from "../components/ProBadge";
import UpgradeButton from "../components/UpgradeButton";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_LABELS = [
  "Table", "Tablecloth", "Signage", "Price sheet", "Product inventory",
  "Payment method", "Cash / change", "Bags", "Business cards", "QR code",
  "Phone charger", "Portable charger", "Extension cord", "Water / snacks",
  "Chair", "Weather protection", "Trash bag", "Tape", "Scissors", "Pens", "Notebook",
];

const buildDefaultItems = () =>
  DEFAULT_LABELS.map((label, i) => ({ id: `default-${i}`, label, custom: false }));

export default function Checklist() {
  const { pro, token, openUpgrade } = useProState();
  const [items, setItems] = useState(buildDefaultItems);
  const [checkState, setCheckState] = useState({});
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [loadingItems, setLoadingItems] = useState(false);

  // Per-device check-off state (stays in localStorage; not tied to account)
  useEffect(() => { setCheckState(getChecklist("global")); }, []);

  // For Pro+logged-in users, load custom items from backend
  useEffect(() => {
    let cancelled = false;
    if (!pro || !token) {
      setItems(buildDefaultItems());
      return;
    }
    setLoadingItems(true);
    (async () => {
      try {
        const res = await fetch(`${API}/checklist`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data.items) && data.items.length > 0) {
            setItems(data.items);
          } else {
            setItems(buildDefaultItems());
          }
        } else {
          setItems(buildDefaultItems());
        }
      } catch {
        if (!cancelled) setItems(buildDefaultItems());
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pro, token]);

  const persistItems = useCallback(async (nextItems) => {
    if (!pro || !token) return;
    try {
      await fetch(`${API}/checklist`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ items: nextItems }),
      });
    } catch {
      toast.error("Couldn't sync to your account — changes are saved on this device.");
    }
  }, [pro, token]);

  const toggle = (label) => {
    const next = { ...checkState, [label]: !checkState[label] };
    setCheckState(next);
    setChecklistItem("global", label, next[label]);
  };

  const startAdding = () => {
    if (!pro) { openUpgrade(); return; }
    setAdding(true);
  };

  const cancelAdding = () => {
    setAdding(false);
    setNewLabel("");
  };

  const submitNew = (e) => {
    e?.preventDefault?.();
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    if (items.some((i) => i.label.toLowerCase() === trimmed.toLowerCase())) {
      toast.error("That item is already on your list");
      return;
    }
    const next = [
      ...items,
      { id: `custom-${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, label: trimmed, custom: true },
    ];
    setItems(next);
    setNewLabel("");
    setAdding(false);
    persistItems(next);
    toast.success("Item added");
  };

  const onDelete = (item) => {
    const next = items.filter((i) => i.id !== item.id);
    setItems(next);
    persistItems(next);
    toast.success(`Removed "${item.label}"`);
  };

  const reset = () => {
    resetChecklist("global");
    setCheckState({});
    toast.success("Checks cleared");
  };

  const onPrint = () => {
    if (!pro) { openUpgrade(); return; }
    setTimeout(() => window.print(), 50);
  };

  const done = useMemo(() => items.filter((i) => checkState[i.label]).length, [items, checkState]);
  const total = items.length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 pb-24 md:pb-16" data-testid="checklist-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 no-print">
        <div>
          <p className="label-eyebrow">Booth Checklist</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink mt-2">
            Show up fully prepared
          </h1>
          <p className="text-brand-mute text-sm mt-1.5">
            {pro
              ? "Customize your list and tap each item as you pack. Changes save to your account."
              : "Tap each item as you pack. Your check-off progress saves automatically."}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onPrint} className="pop-btn-secondary" data-testid="checklist-print-btn">
            <Printer className="w-4 h-4" /> Print / Save <ProBadge className="ml-1" />
          </button>
          <button onClick={reset} className="pop-btn-secondary" data-testid="checklist-reset-btn">
            <RotateCcw className="w-4 h-4" /> Clear checks
          </button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block mb-6">
        <h1 className="text-3xl font-bold text-black">PopUp Profit™ — Booth Checklist</h1>
        <p className="text-sm text-gray-700 mt-1">Prepared {today} · {done} of {total} items packed</p>
        <p className="text-xs text-gray-500 mt-1">popupprofit.com · A Verbum LLC project</p>
      </div>

      {/* Progress */}
      <div className="pop-card p-5 mb-6 sticky top-20 z-30 bg-white/95 backdrop-blur no-print" data-testid="checklist-progress-card">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 grid place-items-center rounded-2xl bg-brand-green/10 text-brand-green">
              <ListChecks className="w-5 h-5" strokeWidth={2} />
            </span>
            <div>
              <p className="font-heading text-lg font-semibold text-brand-ink leading-tight" data-testid="checklist-progress-text">
                {done} of {total} items complete
              </p>
              <p className="text-xs text-brand-mute mt-0.5">{pct}% ready</p>
            </div>
          </div>
          <div className="font-heading text-2xl font-semibold text-brand-green tabular-nums" data-testid="checklist-progress-pct">{pct}%</div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-brand-cream-soft overflow-hidden">
          <div
            className="h-full bg-brand-green transition-all duration-500 ease-out"
            style={{ width: `${pct}%` }}
            data-testid="checklist-progress-bar"
          />
        </div>
      </div>

      {/* Items */}
      {loadingItems ? (
        <div className="pop-card p-6 flex items-center gap-3 text-brand-mute">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your checklist…
        </div>
      ) : (
        <ul className="space-y-2.5 print:space-y-2" data-testid="checklist-items">
          {items.map((item) => {
            const checked = !!checkState[item.label];
            return (
              <li key={item.id} className="group">
                <div
                  className={`w-full flex items-center gap-3 p-4 rounded-2xl border transition-all print:p-2 print:rounded-none print:border-0 print:border-b print:border-gray-300 ${
                    checked
                      ? "bg-brand-green/[0.07] border-brand-green/30"
                      : "bg-white border-brand-line hover:border-brand-gold/40"
                  }`}
                  data-testid={`checklist-item-${item.id}`}
                >
                  <button
                    onClick={() => toggle(item.label)}
                    aria-pressed={checked}
                    className="flex items-center gap-3 flex-1 text-left"
                  >
                    <span
                      className={`w-6 h-6 rounded-md grid place-items-center border-2 transition-all shrink-0 print:border-black print:rounded-sm ${
                        checked ? "bg-brand-green border-brand-green print:bg-black" : "bg-white border-brand-line"
                      }`}
                    >
                      {checked && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                    </span>
                    <span className={`flex-1 text-base font-medium transition-colors print:text-black ${checked ? "text-brand-mute line-through print:text-gray-500" : "text-brand-ink"}`}>
                      {item.label}
                    </span>
                    {item.custom && (
                      <span className="hidden sm:inline-flex items-center pop-pill bg-brand-cream-soft text-brand-mute border border-brand-line text-[10px] no-print">
                        Custom
                      </span>
                    )}
                  </button>
                  {pro && (
                    <button
                      onClick={() => onDelete(item)}
                      className="w-8 h-8 grid place-items-center rounded-full text-brand-mute hover:text-brand-error hover:bg-brand-error/10 transition-colors shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100 no-print"
                      aria-label={`Delete ${item.label}`}
                      data-testid={`checklist-delete-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Add item — Pro only */}
      {!loadingItems && (
        <div className="mt-5 no-print" data-testid="checklist-add-section">
          {adding && pro ? (
            <form onSubmit={submitNew} className="pop-card p-4 flex items-center gap-2" data-testid="checklist-add-form">
              <input
                autoFocus
                type="text"
                placeholder="e.g. Hand sanitizer, Battery pack…"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                className="pop-input flex-1"
                data-testid="checklist-add-input"
                maxLength={60}
              />
              <button type="submit" className="pop-btn-primary shrink-0" data-testid="checklist-add-save">
                <Plus className="w-4 h-4" /> Save
              </button>
              <button
                type="button"
                onClick={cancelAdding}
                className="w-10 h-10 grid place-items-center rounded-full text-brand-mute hover:bg-brand-cream-soft transition-colors shrink-0"
                aria-label="Cancel"
                data-testid="checklist-add-cancel"
              >
                <X className="w-4 h-4" />
              </button>
            </form>
          ) : (
            <button
              onClick={startAdding}
              className={`w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed py-4 text-sm font-medium transition-all ${
                pro
                  ? "border-brand-line text-brand-mute hover:border-brand-green hover:text-brand-green hover:bg-brand-green/5"
                  : "border-brand-gold/40 text-brand-gold hover:bg-brand-gold/5"
              }`}
              data-testid="checklist-add-btn"
            >
              {pro ? (
                <><Plus className="w-4 h-4" /> Add item</>
              ) : (
                <><Lock className="w-3.5 h-3.5" /> Add item <ProBadge className="ml-0.5" /></>
              )}
            </button>
          )}
          {!pro && (
            <p className="text-xs text-brand-mute mt-3 text-center">
              Pro subscribers can add and remove items, and changes save across all your devices.{" "}
              <button onClick={openUpgrade} className="text-brand-green font-medium hover:underline" data-testid="checklist-upsell-link">
                Upgrade →
              </button>
            </p>
          )}
        </div>
      )}

      {/* Print-only footer */}
      <div className="hidden print:block mt-8 pt-4 border-t border-gray-300 text-xs text-gray-500 text-center">
        © 2026 Verbum LLC · PopUp Profit™ — Know before you show.
      </div>
    </div>
  );
}
